import React, { useState, useEffect } from "react";
import { 
  Button, 
  TextField, 
  Typography, 
  Box, 
  Grid, 
  Chip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Checkbox,
  ListItemText
} from "@mui/material";
import Modal from "components/shared/Modal";
import LoaderButton from "components/shared/LoaderButton";
import { ReactMultiEmail } from "react-multi-email";
import "react-multi-email/dist/style.css";

const MultiAgentListModal = ({ open, onClose, onSave, coverages = [] }) => {
  const [agents, setAgents] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [availableCoverages, setAvailableCoverages] = useState([]);
  console.log('agents', agents);

  // Initialize agents array with exactly one row per coverage
  useEffect(() => {
    if (open && coverages.length > 0) {
      // Store full coverage objects in availableCoverages
      setAvailableCoverages([...coverages]);
      
      // Create one row per coverage initially
      const initialAgents = Array(coverages.length).fill().map(() => ({
        name: "",
        agentEmails: [], // Changed from email to agentEmails array
        selectedCoverages: []
      }));
      
      setAgents(initialAgents);
      setErrors({});
    }
  }, [coverages, open]);

  const handleAgentChange = (index, field, value) => {
    const newAgents = [...agents];
    newAgents[index][field] = value;
    setAgents(newAgents);

    // Clear error for this field if it exists
    if (errors[`${index}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${index}-${field}`];
      setErrors(newErrors);
    }
  };

  // Handle email changes
  const handleEmailChange = (index, emails) => {
    const newAgents = [...agents];
    newAgents[index].agentEmails = emails;
    setAgents(newAgents);

    // Clear error for this field if it exists
    if (errors[`${index}-agentEmails`]) {
      const newErrors = { ...errors };
      delete newErrors[`${index}-agentEmails`];
      setErrors(newErrors);
    }
  };

  const handleCoverageChange = (index, selectedCoverages) => {
    let newAgents = [...agents];
    
    // Update the selected coverages for this row
    newAgents[index].selectedCoverages = selectedCoverages;
    
    // Calculate total selected coverages across all rows
    const allSelectedCoverages = newAgents.flatMap(agent => agent.selectedCoverages);
    
    // Determine which coverages are still unselected
    const unselectedCoverages = coverages.filter(
      coverage => !allSelectedCoverages.includes(coverage)
    );
    
    // The number of required empty rows exactly equals the number of unselected coverages
    const requiredEmptyRows = unselectedCoverages.length;
    
    // Calculate current empty rows
    const emptyAgents = newAgents.filter(agent => agent.selectedCoverages.length === 0);
    const currentEmptyRows = emptyAgents.length;
    
    // Get all agents with selections
    const agentsWithSelections = newAgents.filter(agent => agent.selectedCoverages.length > 0);
    
    // Rebuild agents array: first include all rows with selections
    newAgents = [...agentsWithSelections];
    
    // Then add exactly requiredEmptyRows empty rows
    // If there are more empty rows than needed, only keep what's required
    // If there are fewer empty rows than needed, add more to reach the required number
    if (requiredEmptyRows > 0) {
      // First use existing empty rows, up to the required number
      const existingEmptyToKeep = emptyAgents.slice(0, requiredEmptyRows);
      
      // Add new empty rows if needed
      const additionalEmptyRowsNeeded = requiredEmptyRows - existingEmptyToKeep.length;
      const additionalEmptyRows = Array(additionalEmptyRowsNeeded).fill().map(() => ({
        name: "",
        agentEmails: [], // Changed from email to agentEmails array
        selectedCoverages: []
      }));
      
      newAgents = [...newAgents, ...existingEmptyToKeep, ...additionalEmptyRows];
    }
    
    // Set the updated agents array
    setAgents(newAgents);

    // Clear coverage errors
    if (errors[`${index}-coverage`]) {
      const newErrors = { ...errors };
      delete newErrors[`${index}-coverage`];
      setErrors(newErrors);
    }
  };

  const getAvailableCoveragesForRow = (rowIndex) => {
    // Get all coverages selected in other rows
    const selectedInOtherRows = agents.reduce((acc, agent, idx) => {
      if (idx !== rowIndex) {
        return [...acc, ...agent.selectedCoverages];
      }
      return acc;
    }, []);
    
    // Return coverages not selected in other rows, plus what's already selected in current row
    return availableCoverages.filter(coverage => 
      !selectedInOtherRows.some(selected => 
        (selected.type && coverage.type && selected.type === coverage.type) || 
        (!selected.type && !coverage.type && selected === coverage)
      ) || 
      agents[rowIndex]?.selectedCoverages?.some(selected => 
        (selected.type && coverage.type && selected.type === coverage.type) || 
        (!selected.type && !coverage.type && selected === coverage)
      )
    );
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    // Validate all fields
    const newErrors = {};
    let hasError = false;

    agents.forEach((agent, index) => {
      // Skip empty rows
      if (agent.selectedCoverages.length === 0) return;
      
      if (!agent.name.trim()) {
        newErrors[`${index}-name`] = "Name is required";
        hasError = true;
      }
      
      // Validate emails
      if (!agent.agentEmails || agent.agentEmails.length === 0) {
        newErrors[`${index}-agentEmails`] = "At least one email is required";
        hasError = true;
      } else {
        // Check if all emails are valid
        const hasInvalidEmail = agent.agentEmails.some(email => !validateEmail(email));
        if (hasInvalidEmail) {
          newErrors[`${index}-agentEmails`] = "One or more emails are not valid";
          hasError = true;
        }
      }
    });

    // Check if all coverages are selected
    const allSelectedCoverages = agents.flatMap(agent => agent.selectedCoverages);
    const allCoveragesSelected = coverages.every(coverage => 
      allSelectedCoverages.some(selected => 
        (selected.type && coverage.type && selected.type === coverage.type) || 
        (!selected.type && !coverage.type && selected === coverage)
      )
    );
    
    if (!allCoveragesSelected) {
      setErrors({
        ...newErrors,
        general: "All coverages must be selected"
      });
      return;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    
    // Transform agents data to the expected format for the parent component
    const transformedData = agents
      .filter(agent => agent.selectedCoverages.length > 0) // Only include agents with selected coverages
      .map(agent => ({
        name: agent.name,
        agentEmails: agent.agentEmails, // Now using the array of emails
        requestedCoverages: agent.selectedCoverages.map(coverage => 
          coverage.type || coverage
        )
      }));
    
    await onSave(transformedData);
    setSaving(false);
  };

  // Add a function to check if all coverages are selected
  const areAllCoveragesSelected = () => {
    const allSelectedCoverages = agents.flatMap(agent => agent.selectedCoverages);
    return coverages.every(coverage => 
      allSelectedCoverages.some(selected => 
        (selected.type && coverage.type && selected.type === coverage.type) || 
        (!selected.type && !coverage.type && selected === coverage)
      )
    );
  };

  // Handle cancel action - go back to previous modal
  const handleCancel = () => {
    // Reset the current modal data
    setAgents([]);
    setErrors({});
    
    // Close this modal and trigger the previous modal to show
    onClose();

  };

  return (
    <Modal
      open={open}
      title="Match Each Coverage Type with Its Insurance Agent"
      maxWidth="md" // Increase modal width
      fullWidth={true}
      content={
        <>
          {errors.general && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {errors.general}
            </Typography>
          )}
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" fontWeight="bold">
                Agent / Broker Name
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" fontWeight="bold">
                Agent / Broker Email Addresses
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" fontWeight="bold">
                Coverage Types
              </Typography>
            </Grid>
          </Grid>
          
          {agents.map((agent, index) => (
            <Paper 
              key={index} 
              elevation={0} 
              sx={{ 
                mb: 2, 
                p: 1, 
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    placeholder="Name"
                    size="small"
                    value={agent.name}
                    onChange={(e) => handleAgentChange(index, "name", e.target.value)}
                    error={!!errors[`${index}-name`]}
                    helperText={errors[`${index}-name`] || ""}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl 
                    fullWidth 
                    error={!!errors[`${index}-agentEmails`]} 
                    size="small"
                    sx={{ 
                      '& .react-multi-email': {
                        borderColor: errors[`${index}-agentEmails`] ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        minHeight: '38px',
                      }
                    }}
                  >
                    <div className="react-multi-email-outer">
                      <ReactMultiEmail
                        placeholder="Enter email addresses"
                        emails={agent.agentEmails}
                        onChange={(emails) => handleEmailChange(index, emails)}
                        validateEmail={email => {
                          return validateEmail(email);
                        }}
                        getLabel={(email, index, removeEmail) => {
                          return (
                            <Chip 
                              key={index} 
                              color="primary" 
                              size="small" 
                              label={email} 
                              onDelete={() => removeEmail(index)} 
                              sx={{ borderRadius: '16px', margin: '2px' }}
                            />
                          );
                        }}
                      />
                    </div>
                    {errors[`${index}-agentEmails`] && (
                      <FormHelperText error>{errors[`${index}-agentEmails`]}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small" error={!!errors[`${index}-coverage`]}>
                    <Select
                      multiple
                      value={agent.selectedCoverages}
                      onChange={(e) => handleCoverageChange(index, e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: '60px', overflow: 'auto' }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value.type || value} 
                              label={value.label || value}
                              size="small"
                              color="primary"
                              sx={{ borderRadius: '16px', margin: '1px' }}
                            />
                          ))}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 250,
                          },
                        },
                      }}
                    >
                      {getAvailableCoveragesForRow(index).map((coverage) => {
                        const isSelected = agent.selectedCoverages.some(
                          selected => 
                            (selected.type && coverage.type && selected.type === coverage.type) || 
                            (!selected.type && !coverage.type && selected === coverage)
                        );
                        
                        return (
                          <MenuItem key={coverage.type || coverage} value={coverage}>
                            <Checkbox 
                              checked={isSelected} 
                              sx={{ p: 0, mr: 1 }}
                              color="primary"
                            />
                            <ListItemText primary={coverage.label || coverage} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {!!errors[`${index}-coverage`] && (
                      <FormHelperText>{errors[`${index}-coverage`]}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              Note: You must assign all coverage types before submitting.
            </Typography>
          </Box>
        </>
      }
      actions={
        <>
          <Button onClick={handleCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          <LoaderButton
            loading={saving}
            onClick={handleSubmit}
            color="primary" 
            variant="contained"
            disabled={!areAllCoveragesSelected()}
          >
            Submit
          </LoaderButton>
        </>
      }
    />
  );
};

export default MultiAgentListModal;